import { ChangeEvent, CSSProperties } from "react";

import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

import styles from "./TextAreaResizable.module.scss";

interface ITextAreaResizableProps {
    id: string;
    dataCy?: string;
    text: string;
    isDisabled?: boolean;
    setCursorStartOnFocus?: boolean;
    verticalResizeOnly?: boolean;
    minHeightPx?: number;
    maxHeightPx?: number;
    invalid?: boolean;
    rows?: number;
    maxRows?: number;
    containerClassName?: string;

    debounceMs?: number

    onChange?: (value: string) => void;
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    operation?: number;
    resetTextOnIdChange?: boolean;
    operationUnMount?: number;
    IsSmall?: boolean;
    style?: CSSProperties;

    operationArg1?: any;
    operationArg2?: any;
    operationArg3?: any;
    keepWhiteOnDisabled?: boolean;

    maxLength?: number;
    className?: string;

    showCounter?: boolean;
    counterClassName?: string;
}

class TextAreaResizableState {
    public value: string;
    public defaultHeight: number;

    constructor() {
        this.value = "";
        this.defaultHeight = 0;
    }
}

class TextAreaResizable extends React.Component<ITextAreaResizableProps, TextAreaResizableState> {
    private minHeightPx: number;
    private maxHeightPx: number;
    private flagForStateReset: boolean;
    private dispatchReference: string;
    private timeoutReference: number;
    private onResize: () => void;

    constructor(props: ITextAreaResizableProps) {
        super(props);

        this.flagForStateReset = false;

        this.minHeightPx = this.props.minHeightPx ?? 144;
        this.maxHeightPx = this.props.maxHeightPx ?? 300;

        const initialState = new TextAreaResizableState();
        if (this.props.text != null) {
            initialState.value = this.props.text ? this.props.text : "";
        }
        this.state = initialState;
    }

    public componentDidMount(): void {
        this.initDispatchSubscription();
        this.applyHeightToControl();
        this.onResize = () => this.applyHeightToControl();
        window.addEventListener("resize", this.onResize);
    }

    public shouldComponentUpdate(nextProps: ITextAreaResizableProps, nextState: TextAreaResizableState): boolean {
        const isSameId = (this.props.id !== nextProps.id);

        if (isSameId && this.props.resetTextOnIdChange) {
            this.flagForStateReset = true;
        }

        return (
            (this.props.isDisabled !== nextProps.isDisabled) ||
            isSameId ||
            (this.state.value !== nextState.value) ||
            (this.props.text !== nextProps.text) ||
            (this.props.invalid !== nextProps.invalid)
        );
    }

    public componentDidUpdate(): void {
        if (this.props.resetTextOnIdChange && this.flagForStateReset) {
            this.flagForStateReset = false;

            const newState = new TextAreaResizableState();

            if (this.props.text) {
                newState.value = this.props.text;
            }
            else {
                newState.value = "";
            }

            this.setState(newState);
        }

        this.applyHeightToControl();
    }

    public render(): JSX.Element {
        let formControl = this.props.IsSmall ? "form-control input-sm" : "form-control";

        let css = this.props.verticalResizeOnly === true ? formControl + " vresize" : formControl;
        if (this.props.keepWhiteOnDisabled)
            css += " keep-white-on-disable";

        if (this.props.invalid) {
            css += " invalid";
        }
        return (
            <div className={`${styles.container} ${this.props.containerClassName}`}>
                <textarea
                    onFocus={() => this.onFocus?.()}
                    onBlur={e => this.props.onBlur?.(e)}
                    onChange={e => this.onTextChange?.(e)}
                    onKeyDown={e => this.onKeyDown(e)}
                    id={this.props.id}
                    data-cy={this.props.dataCy}
                    disabled={this.props.isDisabled}
                    className={`${css} ${this.props.className ?? ""}`}
                    value={this.state.value}
                    style={this.props.style}
                    maxLength={this.props.maxLength}
                    rows={this.props.rows}
                />
                {this.props.showCounter && (
                    <span className={`${styles.counter} ${this.props.counterClassName}`}>
                        {`${this.state.value.length}/${this.props.maxLength}`}
                    </span>
                )}
            </div>
        );
    }

    private initDispatchSubscription(): void {
        this.dispatchReference = LocalEventDispatcher.register((request: LocalEvent) => {
            if (request.Operation !== LocalOperations.Misc.ClickableTextControlAppend)
                return;

            const SpecificTextControlId = request.Get<string>("SpecificTextControlId");
            const ApplyOnlyIfFieldIsNullOrEmpty = request.Get<boolean>("ApplyOnlyIfFieldIsNullOrEmpty");

            if ((this.props.id === sessionStore.lastFocusedClickableTextController && SpecificTextControlId == null) /* Send to last focused */ ||
                (this.props.id === SpecificTextControlId && SpecificTextControlId != null)) { // Send specifically to this Id
                if (ApplyOnlyIfFieldIsNullOrEmpty && this.state.value != null && this.state.value != "")
                    return;

                const val = request.Get<string>("value");
                const s = new TextAreaResizableState();

                if (this.state.value == null || this.state.value == "")
                    s.value = val;
                else
                    s.value = this.state.value + "\r\n" + val;

                this.setState(s);

                window.setTimeout(() => {
                    if (this.props.operation != null)
                        sessionStore.dispatch(this.props.operation, { newText: this.state.value });

                    this.props.onChange?.(s.value);
                }, 50);
            }
        });
    }

    private applyHeightToControl(): void {
        const textArea = document.getElementById(this.props.id);

        if (textArea) {
            textArea.style.height = "0";
            const height = this.calculateAreaHeight(textArea.scrollHeight);
            textArea.style.height = height + "px";
        }
    }

    private calculateAreaHeight(scrollHeight: number): number {
        const padding = 5;

        // fit within min and max height
        let height = Math.max(scrollHeight + padding, this.minHeightPx);
        height = Math.min(height, this.maxHeightPx);

        return height;
    }

    private onFocus(): void {
        sessionStore.lastFocusedClickableTextController = this.props.id;

        if (this.props.setCursorStartOnFocus === true) {
            const elem: any = document.getElementById(this.props.id);
            const txt: HTMLTextAreaElement = elem;
            txt.setSelectionRange(0, 0);
        }
    }

    private onTextChange(e: ChangeEvent<HTMLTextAreaElement>): void {
        let value = e.target.value;

        if (this.props.maxLength !== undefined && value.length > this.props.maxLength) {
            value = value.slice(0, this.props.maxLength);
        }

        clearTimeout(this.timeoutReference);
        this.timeoutReference = window.setTimeout(
            () => this.invokeCallback(value),
            this.props.debounceMs == null ? 200 : this.props.debounceMs
        );

        const state = new TextAreaResizableState();
        state.value = value;
        this.setState(state);
    }

    public componentWillUnmount(): void {
        LocalEventDispatcher.unregister(this.dispatchReference);
        window.removeEventListener("resize", this.onResize);
    }

    private invokeCallback(value: string): void {
        this.props.onChange?.(value);

        if (this.props.operation != null) {
            sessionStore.dispatch(this.props.operation, {
                newText: value,
                value: value,
                arg1: this.props.operationArg1,
                arg2: this.props.operationArg2,
                arg3: this.props.operationArg3
            });
        }
    }

    private onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
        if (e.keyCode === 13 && this.isNewlineExceedingMaxRow())
            e.preventDefault();

        this.props.onKeyDown?.(e);
    }

    private isNewlineExceedingMaxRow(): boolean {
        return this.props.maxRows
            && this.props.maxRows <= (this.state.value?.split("\n").length ?? 0);
    }
}

export { TextAreaResizable };
